import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

interface ReworkBarChartProps {
  data: Array<{
    category: string;
    categoryLabel: string;
    avgReworkCount: number;
    issueCount: number;
  }>;
  isLoading?: boolean;
}

const getBarColor = (value: number): string => {
  if (value >= 2) return "hsl(0, 84%, 60%)";      // Red - high rework
  if (value >= 1) return "hsl(48, 96%, 53%)";    // Yellow - moderate
  return "hsl(142, 76%, 36%)";                   // Green - low rework
};

export function ReworkBarChart({ data, isLoading = false }: ReworkBarChartProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-5 w-48" />
          <Skeleton className="h-4 w-64 mt-1" />
        </CardHeader>
        <CardContent className="h-80">
          <div className="flex items-center justify-center h-full">
            <Skeleton className="h-64 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Rework by Category</CardTitle>
          <CardDescription>Average rework count per issue category</CardDescription>
        </CardHeader>
        <CardContent className="h-80">
          <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
            <p className="text-sm">No category data available</p>
            <p className="text-xs mt-1">Rework rates will appear as issues are processed</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Show top 8 categories by rework rate
  const chartData = data.slice(0, 8);

  return (
    <Card data-testid="chart-rework-by-category">
      <CardHeader>
        <CardTitle>Rework by Category</CardTitle>
        <CardDescription>Average rework count per issue category</CardDescription>
      </CardHeader>
      <CardContent className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
            <XAxis type="number" domain={[0, 'auto']} />
            <YAxis
              dataKey="categoryLabel"
              type="category"
              width={120}
              tick={{ fontSize: 12 }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: "hsl(var(--card))",
                border: "1px solid hsl(var(--border))",
                borderRadius: "0.5rem",
              }}
              formatter={(value: number, name: string, props: any) => [
                `${value.toFixed(2)} avg rework (${props.payload.issueCount} issues)`,
                "",
              ]}
            />
            <Bar dataKey="avgReworkCount" name="Avg Rework">
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={getBarColor(entry.avgReworkCount)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
